import React from 'react'
import PropTypes from 'prop-types'
import {
  Label,
  Button,
  ButtonGroup,
  ButtonGroupItem,
  SelectField,
  TextareaField,
  SecondaryButton,
  Columns,
  Column,
  LightMessage,
  MessageHeader,
  MessageBody,
} from '../../bulma'

/**
* Form to receive feedback related to search results. Results are aggregated in
* Google Analytics.
* @TODO Improve transitions between states. (animations or something maybe?)
*/
class SearchFeedback extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      isSubmitted: false,
      message: '',
      category: null,
      isPositive: null,
    }

    this.inputChangeHandler = this.inputChangeHandler.bind(this)
    this.formSubmitHandler = this.formSubmitHandler.bind(this)
    this.renderForm = this.renderForm.bind(this)
    this.renderSubmitted = this.renderSubmitted.bind(this)
  }

  /**
  * Manages form's state based on name/value received. If the field is the
  * `isPositive` field, then the action is tracked in Google Analytics as well.
  */
  inputChangeHandler(name, value) {
    this.setState({ [name]: value })
  }

  /**
  * Handles form submission to Google Analyics.
  */
  formSubmitHandler(e) {
    e.preventDefault()
    const eventCategory = 'Search Feedback'
    const eventAction = `${this.state.isPositive ? 'Positive' : this.state.category}`
    const eventLabel = this.state.message

    $.track('event', eventCategory, eventAction, eventLabel)

    this.setState({ isSubmitted: true })
  }

  /**
  * Renders the search feedback form.
  */
  renderForm() {
    // Positive/negative rating field.
    const experienceLabel = (
      <Label size="small">Did you find what you were looking for?</Label>
    )
    const experienceInput = (
      <ButtonGroup>
        <ButtonGroupItem>
          <Button
            size="small"
            isActive={this.state.isPositive === true}
            onClick={() => this.inputChangeHandler('isPositive', true)}
            trackEvent
            eventCategory="Search Feedback"
            eventAction="Rating"
            eventLabel="Positive"
          >
            Yes
          </Button>
        </ButtonGroupItem>
        <ButtonGroupItem>
          <Button
            size="small"
            isActive={this.state.isPositive === false}
            onClick={() => this.inputChangeHandler('isPositive', false)}
            trackEvent
            eventCategory="Search Feedback"
            eventAction="Rating"
            eventLabel="Negative"
          >
            No
          </Button>
        </ButtonGroupItem>
      </ButtonGroup>
    )

    // Category field. Only shown for negative ratings.
    const categoryInput = this.state.isPositive === false ? (
      <SelectField
        name="category"
        size="small"
        label="Choose a category that best describes the issue you're having."
        options={[
          { value: 'Trouble Finding Product', label: 'I can\'t find what I\'m looking for.' },
          { value: 'Bug Report', label: 'Something is broken.' },
          { value: 'Wrong Information', label: 'A product\'s information looks wrong.' },
          { value: 'Feature Request', label: 'I have an idea for a new feature.' },
          { value: 'Other', label: 'Other' },
        ]}
        value={this.state.category}
        onChange={this.inputChangeHandler}
      />
    ) : false

    // Message field. Only shown after experience has been rated.
    const messageInput = this.state.isPositive !== null ? (
      <TextareaField
        name="message"
        size="small"
        label="How can we make it better? Your comments help us improve your experience."
        value={this.state.message}
        onChange={this.inputChangeHandler}
        inputProps={{ rows: '4', placeholder: 'We will not be able to respond to your feedback through this form. Contact Customer Service if you need help or have a question.' }}
      />
    ) : false

    const submitButton = this.state.isPositive !== null ? (
      <SecondaryButton size="small" disabled={!this.state.message} type="submit">Submit</SecondaryButton>
    ) : false

    return (
      <form onSubmit={this.formSubmitHandler}>
        {experienceLabel}
        {experienceInput}
        {categoryInput}
        {messageInput}
        {submitButton}
      </form>
    )
  }

  /**
  * Success message displayed once the form has been submitted.
  */
  renderSubmitted() {
    return (
      <div className="has-text-centered">
        <strong>Thank you!</strong> Your feedback will help make our site better for everyone.
      </div>
    )
  }

  render() {
    return (
      <Columns>
        <Column>
          <LightMessage>
            <MessageHeader>Search Feedback</MessageHeader>
            <MessageBody>
              {
                this.state.isSubmitted
                ? this.renderSubmitted()
                : this.renderForm()
              }
            </MessageBody>
          </LightMessage>
        </Column>
      </Columns>
    )
  }
}

export default SearchFeedback
